import { useMemo, useState } from 'react';
import { Modal, Button } from '@/components';
import type { EmailRecipient, PickedContact } from '@/lib/apiClient';
import RecipientsField, { mailtoFor, reusableRecipients, startingRecipients } from './contacts/RecipientsField';
import { useJobContacts } from './contacts/jobContacts';
import { fmtDate } from '@/lib/dates';
import { dueStatus } from '@/lib/dueStatus';

/**
 * Client-side Outlook draft composer for RFIs (PM-controlled).
 *
 * Mirrors SubmittalEmailModal: build an editable Subject + Body, open a
 * `mailto:` href ("✉ Open in Outlook"), offer "Copy subject"/"Copy body" via the
 * clipboard, and warn when the encoded mailto runs long (clients truncate the
 * body past ~2000 chars). The app never sends mail and makes no API call here —
 * the PM attaches any markups and sends from Outlook.
 *
 * ⚠️ Opening this does NOT record a send (#608). It used to follow a call that
 * stamped the send before the draft existed, so the record said "sent" while
 * the email sat in Drafts. The parent now asks afterwards, with the time —
 * which is why, unlike SubmittalEmailModal, onClose reports whether the draft
 * was used.
 *
 * Two modes:
 *   • send     — cover note sending the RFI question to the assignee.
 *   • reminder — polite nudge keyed to the response-due date (overdue / due soon).
 *
 * To and CC are picked from the job's contacts or typed (#598 step 2); an
 * assignee who is a job contact starts on the To line. They're handed back on
 * close, so the "Did you send it?" step can record who the send went to.
 */

interface RfiEmailRfi {
  id: number;
  rfiNumber: string;
  subject: string;
  specSection: string | null;
  question: string | null;
  assignee: string | null;
  assigneeContact?: PickedContact | null;
  dueDate: string | null;
}

interface RfiEmailProject {
  jobNumber: string;
  title: string;
}

export interface RfiEmailModalProps {
  rfi: RfiEmailRfi;
  project: RfiEmailProject;
  /** The job, for its contacts. */
  projectId: number;
  /**
   * Who the RFI's send went to, for a reminder: it starts addressed to them
   * rather than to the assignee alone, so whoever was copied isn't dropped (review).
   */
  previousRecipients?: EmailRecipient[];
  mode: 'send' | 'reminder';
  /**
   * `used` is whether the draft went anywhere — "Open in Outlook" clicked, or
   * the subject or body copied. For a send, the parent asks "did you send it?"
   * only then (#608); a composer closed untouched cannot have sent anything.
   * `recipients` is who the draft was addressed to when it was closed.
   */
  onClose: (used: boolean, recipients: EmailRecipient[]) => void;
}

const css = `
  .rfi-em-field { display: flex; flex-direction: column; gap: 4px; }
  .rfi-em-field label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase; letter-spacing: var(--ew-ls-caption); color: var(--ew-n-600);
  }
  .rfi-em-input, .rfi-em-textarea {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font);
    padding: 8px 10px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md);
    background: #fff; color: var(--ew-dark); width: 100%; box-sizing: border-box;
  }
  .rfi-em-textarea { resize: vertical; min-height: 220px; font-family: var(--ew-font); }
  .rfi-em-label-row { display: flex; justify-content: space-between; align-items: center; }
  .rfi-em-copied { color: #027835; font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font); }
  .rfi-em-linkbtn {
    background: transparent; border: 0; cursor: pointer; padding: 0;
    color: var(--ew-n-600); font: var(--ew-fw-medium) var(--ew-fs-caption) var(--ew-font);
    text-decoration: underline; text-underline-offset: 2px;
  }
  .rfi-em-linkbtn:hover { color: var(--ew-dark); }
  .rfi-em-hint { color: var(--ew-n-500); font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); }
  .rfi-em-warn { color: #8a5a00; font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); }
  .rfi-em-error { color: #8F2414; font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); }
  .rfi-em-footer-note { margin-right: auto; max-width: 320px; }
`;

function buildSendSubject(rfi: RfiEmailRfi, project: RfiEmailProject): string {
  return `RFI ${rfi.rfiNumber}: ${rfi.subject} — ${project.jobNumber} ${project.title}`;
}

function buildSendBody(rfi: RfiEmailRfi): string {
  const lines: string[] = [];
  lines.push('Hello,');
  lines.push('');
  lines.push('The following RFI requires your response:');
  lines.push('');
  lines.push(`  • RFI #: ${rfi.rfiNumber}`);
  lines.push(`  • Subject: ${rfi.subject}`);
  if (rfi.specSection) lines.push(`  • Spec section: ${rfi.specSection}`);
  lines.push('');
  if (rfi.question) {
    lines.push('Question:');
    lines.push(rfi.question);
    lines.push('');
  }
  if (rfi.dueDate) {
    lines.push(`Please respond by ${fmtDate(rfi.dueDate)}.`);
    lines.push('');
  }
  lines.push('Please let me know if you have any questions or need anything further.');
  lines.push('');
  lines.push('Thank you,');
  return lines.join('\n');
}

function buildReminderSubject(rfi: RfiEmailRfi, project: RfiEmailProject): string {
  const { daysUntil } = dueStatus(rfi.dueDate);
  const overdue = daysUntil != null && daysUntil < 0;
  const tail = overdue ? 'Overdue' : `Response due ${fmtDate(rfi.dueDate)}`;
  return `Reminder — RFI ${rfi.rfiNumber} — ${tail} — ${project.jobNumber}`;
}

function buildReminderBody(rfi: RfiEmailRfi, project: RfiEmailProject): string {
  const { daysUntil } = dueStatus(rfi.dueDate);
  const lines: string[] = [];
  lines.push('Hello,');
  lines.push('');

  let statusSentence: string;
  if (daysUntil == null) {
    statusSentence = `I wanted to follow up on RFI ${rfi.rfiNumber} ("${rfi.subject}") for ${project.jobNumber} ${project.title}.`;
  } else if (daysUntil < 0) {
    const n = Math.abs(daysUntil);
    statusSentence = `RFI ${rfi.rfiNumber} ("${rfi.subject}") for ${project.jobNumber} ${project.title} was due on ${fmtDate(rfi.dueDate)} and a response is now overdue by ${n} day${n === 1 ? '' : 's'}.`;
  } else if (daysUntil === 0) {
    statusSentence = `A response to RFI ${rfi.rfiNumber} ("${rfi.subject}") for ${project.jobNumber} ${project.title} is due today (${fmtDate(rfi.dueDate)}).`;
  } else {
    statusSentence = `A response to RFI ${rfi.rfiNumber} ("${rfi.subject}") for ${project.jobNumber} ${project.title} is due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}, on ${fmtDate(rfi.dueDate)}.`;
  }
  lines.push(statusSentence);
  lines.push('');
  lines.push('Could you please let me know the status of your response, or send it over when you have a chance?');
  lines.push('');
  lines.push('Thank you for your help,');
  return lines.join('\n');
}

const MODE_TITLE: Record<RfiEmailModalProps['mode'], string> = {
  send: 'Send RFI with Outlook',
  reminder: 'Send a reminder',
};

function buildSubjectFor(mode: RfiEmailModalProps['mode'], rfi: RfiEmailRfi, project: RfiEmailProject): string {
  if (mode === 'reminder') return buildReminderSubject(rfi, project);
  return buildSendSubject(rfi, project);
}

function buildBodyFor(mode: RfiEmailModalProps['mode'], rfi: RfiEmailRfi, project: RfiEmailProject): string {
  if (mode === 'reminder') return buildReminderBody(rfi, project);
  return buildSendBody(rfi);
}

export default function RfiEmailModal({ rfi, project, projectId, mode, onClose, previousRecipients }: RfiEmailModalProps) {
  // The opener waits for these before offering a reminder or a revision, so they are
  // here (from the shared query) when the recipients below are first worked out.
  const { options: contacts, isSuccess: contactsKnown } = useJobContacts(projectId);
  const [recipients, setRecipients] = useState<EmailRecipient[]>(() =>
    {
    const reused = previousRecipients?.length ? reusableRecipients(previousRecipients, contactsKnown ? contacts : null) : [];
    return reused.length ? reused : startingRecipients(rfi.assigneeContact);
  });
  const [subject, setSubject] = useState(() => buildSubjectFor(mode, rfi, project));
  const [body, setBody] = useState(() => buildBodyFor(mode, rfi, project));
  const [copied, setCopied] = useState<'subject' | 'body' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [used, setUsed] = useState(false);

  // mailto: draft link (opens the OS default mail client, e.g. Outlook desktop).
  // To and CC as picked, encoded subject + body — same mechanics as SubmittalEmailModal.
  const mailtoHref = useMemo(() => mailtoFor(recipients, subject, body), [recipients, subject, body]);
  // mailto URLs get truncated by many clients past ~2000 chars (the encoded body
  // inflates length); warn so the PM uses Copy for long emails.
  const mailtoTooLong = mailtoHref.length > 1900;

  async function copy(which: 'subject' | 'body') {
    try {
      await navigator.clipboard.writeText(which === 'subject' ? subject : body);
      setCopied(which);
      setUsed(true);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setError('Could not access the clipboard — select the text and copy manually.');
    }
  }

  return (
    <>
      <style>{css}</style>
      <Modal
        isOpen
        onClose={() => onClose(used, recipients)}
        maxWidth={720}
        title={MODE_TITLE[mode]}
        footer={
          <>
            <span className="rfi-em-hint rfi-em-footer-note">Attach any markups in Outlook before sending.</span>
            <a className="btn btn-primary ew-btn-sm" href={mailtoHref} target="_blank" rel="noopener noreferrer" onClick={() => setUsed(true)} onAuxClick={(e) => { if (e.button === 1) setUsed(true); }}>
              ✉ Open in Outlook
            </a>
            <Button variant="secondary" size="sm" onClick={() => onClose(used, recipients)}>Done</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ew-s-3)' }}>
          <RecipientsField contacts={contacts} value={recipients} onChange={setRecipients} />
          {rfi.assigneeContact && !rfi.assigneeContact.email ? (
            <span className="rfi-em-hint">
              Assigned to {rfi.assigneeContact.name ?? rfi.assignee}, who has no email in the address book. Add it on the job’s Contacts tab, or type the address.
            </span>
          ) : !rfi.assigneeContact && rfi.assignee ? (
            <span className="rfi-em-hint">Assigned to: {rfi.assignee}. Pick them from the job’s contacts to address it for you.</span>
          ) : null}

          <div className="rfi-em-field">
            <div className="rfi-em-label-row">
              <label>Subject</label>
              <button type="button" className="rfi-em-linkbtn" onClick={() => copy('subject')}>
                {copied === 'subject' ? <span className="rfi-em-copied">Copied!</span> : 'Copy subject'}
              </button>
            </div>
            <input className="rfi-em-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          <div className="rfi-em-field">
            <div className="rfi-em-label-row">
              <label>Body</label>
              <button type="button" className="rfi-em-linkbtn" onClick={() => copy('body')}>
                {copied === 'body' ? <span className="rfi-em-copied">Copied!</span> : 'Copy body'}
              </button>
            </div>
            <textarea className="rfi-em-textarea" value={body} onChange={(e) => setBody(e.target.value)} />
            {mailtoTooLong && (
              <span className="rfi-em-warn">
                This email is long — "Open in Outlook" may truncate it. Use "Copy body" and paste it in instead.
              </span>
            )}
          </div>

          {error && <div className="rfi-em-error">{error}</div>}
        </div>
      </Modal>
    </>
  );
}
